import { supabase } from '@/integrations/supabase/client';

// ============================================================================
// DATA STRUCTURES
// ============================================================================

export interface SignalHistoryItem {
  pattern_sequence: string;
  win: number | null;
  action: string;
  decision: string;
  created_at: string;
  bot_id: string;
}

export interface SubsequenceStats {
  pattern: string;
  frequency: number;
  winRate: number;
  wins: number;
  losses: number;
  avgPosition: number;
  // NEW: Context tracking for smart filtering
  appearsAfterWin: number;
  appearsAfterLoss: number;
  contextWinRate: number; // Win rate of the NEXT signal after pattern
}

export interface PatternRule {
  lock: string;
  open: string;
  priority: number;
}

export interface SimulationStep {
  signalIndex: number;
  recentResults: string;
  mode: 'OPEN' | 'GATE';
  activeLock: PatternRule | null;
  action: 'TRADE' | 'SKIP' | 'LOCK' | 'UNLOCK';
  win?: number;
  signal: SignalHistoryItem;
}

export interface SimulationResult {
  winRate: number;
  wins: number;
  losses: number;
  totalTrades: number;
  trades: SimulationStep[];
  lockActivations: number;
  skippedSignals: number;
}

export interface ComboSuggestion {
  rank: number;
  rules: PatternRule[];
  winRate: number;
  wins: number;
  losses: number;
  totalTrades: number;
  confidence: number;
  lockActivations: number;
  skippedSignals: number;
  simulationResult: SimulationResult;
}

// ============================================================================
// PHASE 1: DATA FETCHING & SUBSEQUENCE ANALYSIS
// ============================================================================

export async function fetchSignalHistory(
  botId: string | null,
  timeRange: string
): Promise<SignalHistoryItem[]> {
  let query = supabase
    .from('conditional_skip_events')
    .select('pattern_sequence, win, action, decision, created_at, bot_id')
    .eq('response_status', 200)
    .order('created_at', { ascending: true });

  // Apply time range filter
  const now = new Date();
  let startDate: Date;
  
  switch (timeRange) {
    case '7d':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30d':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    case '90d':
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      break;
    default:
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  }
  
  query = query.gte('created_at', startDate.toISOString());

  // Apply bot filter
  if (botId) {
    query = query.eq('bot_id', botId);
  }

  const { data, error } = await query.limit(2000);

  if (error) {
    console.error('Error fetching signal history:', error);
    throw error;
  }

  return data || [];
}

export function extractSubsequences(
  sequence: string,
  minLength: number,
  maxLength: number
): string[] {
  const subsequences = new Set<string>();
  
  for (let len = minLength; len <= Math.min(maxLength, sequence.length); len++) {
    for (let i = 0; i <= sequence.length - len; i++) {
      subsequences.add(sequence.slice(i, i + len));
    }
  }
  
  return Array.from(subsequences);
}

export function analyzeSubsequences(
  signalHistory: SignalHistoryItem[],
  minLength: number,
  maxLength: number
): Map<string, SubsequenceStats> {
  const stats = new Map<string, SubsequenceStats>();
  
  // Build a sequence of wins/losses for each bot
  const botSequences = new Map<string, string[]>();
  
  for (const signal of signalHistory) {
    if (signal.win !== null && (signal.action === 'EXIT_LONG' || signal.action === 'EXIT_SHORT')) {
      if (!botSequences.has(signal.bot_id)) {
        botSequences.set(signal.bot_id, []);
      }
      botSequences.get(signal.bot_id)!.push(signal.win.toString());
    }
  }
  
  // Analyze patterns across all bot sequences
  for (const [botId, sequence] of botSequences.entries()) {
    // Look at each position in the sequence
    for (let i = 0; i < sequence.length; i++) {
      const result = parseInt(sequence[i]); // Current result (0 or 1)
      
      // Extract subsequences leading up to this point
      const lookbackStart = Math.max(0, i - maxLength);
      const lookback = sequence.slice(lookbackStart, i).join('');
      
      // Generate all possible patterns from this lookback
      const patterns = extractSubsequences(lookback, minLength, Math.min(maxLength, lookback.length));
      
      for (const pattern of patterns) {
        // Check if pattern appears at the END of lookback
        if (lookback.endsWith(pattern)) {
          if (!stats.has(pattern)) {
            stats.set(pattern, {
              pattern,
              frequency: 0,
              winRate: 0,
              wins: 0,
              losses: 0,
              avgPosition: 0,
              appearsAfterWin: 0,
              appearsAfterLoss: 0,
              contextWinRate: 0
            });
          }
          
          const stat = stats.get(pattern)!;
          stat.frequency++;
          
          // Track context: What came BEFORE this pattern?
          if (i > 0) {
            const previousResult = parseInt(sequence[i - 1]);
            if (previousResult === 1) {
              stat.appearsAfterWin++;
            } else {
              stat.appearsAfterLoss++;
            }
          }
          
          // Track what comes AFTER this pattern (for contextWinRate)
          if (result === 1) {
            stat.wins++;
          } else {
            stat.losses++;
          }
        }
      }
    }
  }
  
  // Calculate win rates and context metrics
  for (const [pattern, stat] of stats.entries()) {
    const total = stat.wins + stat.losses;
    stat.winRate = total > 0 ? (stat.wins / total) * 100 : 0;
    stat.contextWinRate = total > 0 ? (stat.wins / total) * 100 : 0;
  }
  
  return stats;
}

// ============================================================================
// PHASE 2: COMBO GENERATION
// ============================================================================

/**
 * Generate all possible Lock/Open pairs (brute-force approach)
 */
function generateAllPairs(
  patterns: SubsequenceStats[]
): Array<{ lock: string; open: string }> {
  const pairs: Array<{ lock: string; open: string }> = [];
  
  for (const lockPattern of patterns) {
    for (const openPattern of patterns) {
      // Can use same pattern for lock and open
      pairs.push({
        lock: lockPattern.pattern,
        open: openPattern.pattern
      });
    }
  }
  
  return pairs;
}

/**
 * Check if a combo has duplicate lock patterns
 * Each lock pattern should only appear ONCE in a combo
 */
function hasUniqueLocks(combo: Array<{ lock: string; open: string }>): boolean {
  const seenLocks = new Set<string>();
  
  for (const pair of combo) {
    if (seenLocks.has(pair.lock)) {
      return false; // Duplicate found!
    }
    seenLocks.add(pair.lock);
  }
  
  return true;
}

/**
 * Generate combinations of pairs (brute-force)
 * CRITICAL: Each lock pattern can only appear ONCE per combo
 */
function generatePairCombinations(
  pairs: Array<{ lock: string; open: string }>,
  comboSize: number,
  maxCombos: number = 2000
): Array<{ lock: string; open: string }>[] {
  const combinations: Array<{ lock: string; open: string }>[] = [];
  
  // Helper function for recursive combination generation
  function generate(
    current: Array<{ lock: string; open: string }>,
    start: number
  ) {
    if (current.length === comboSize) {
      // ✅ Validate: Each lock pattern must be unique
      if (hasUniqueLocks(current)) {
        combinations.push([...current]);
      }
      return;
    }
    
    if (combinations.length >= maxCombos) return;
    
    for (let i = start; i < pairs.length && combinations.length < maxCombos; i++) {
      // ✅ Early pruning: Skip if this pair would create duplicate lock
      const wouldHaveDuplicate = current.some(p => p.lock === pairs[i].lock);
      if (wouldHaveDuplicate) {
        continue; // Skip this pair
      }
      
      current.push(pairs[i]);
      generate(current, i + 1);
      current.pop();
    }
  }
  
  generate([], 0);
  return combinations;
}

export function generateComboCandidates(
  subsequenceStats: Map<string, SubsequenceStats>,
  comboSize: number,
  minFrequency: number,
  patternMinLength: number,
  patternMaxLength: number
): PatternRule[][] {
  console.log('🔍 Starting Smart Combo Generation (Option B)');
  
  // Step 1: Filter patterns by frequency and length
  const allPatterns = Array.from(subsequenceStats.values()).filter(
    stat => stat.frequency >= minFrequency && 
            stat.pattern.length >= patternMinLength &&
            stat.pattern.length <= patternMaxLength
  );
  
  console.log(`📊 Total patterns after filtering: ${allPatterns.length}`);
  
  if (allPatterns.length < 2) {
    console.warn('Not enough patterns for combo generation');
    return [];
  }
  
  // Step 2: Dynamic percentile-based filtering
  // Sort all patterns by contextWinRate
  const sortedByContext = [...allPatterns].sort((a, b) => 
    a.contextWinRate - b.contextWinRate
  );

  // Lock candidates: bottom 30% (worst contexts) with minimum of 5
  const lockCount = Math.max(5, Math.floor(allPatterns.length * 0.3));
  const lockCandidates = sortedByContext.slice(0, lockCount);

  // Open candidates: top 30% (best contexts) with minimum of 5
  const openCount = Math.max(5, Math.floor(allPatterns.length * 0.3));
  const openCandidates = sortedByContext.slice(-openCount);

  console.log(`🔒 Lock candidates (bottom ${((lockCount/allPatterns.length)*100).toFixed(0)}%): ${lockCandidates.length}`);
  console.log(`   Range: ${lockCandidates[0]?.contextWinRate.toFixed(3)} - ${lockCandidates[lockCandidates.length-1]?.contextWinRate.toFixed(3)}`);
  console.log(`   Patterns:`, lockCandidates.map(c => ({ pattern: c.pattern, contextWR: c.contextWinRate?.toFixed(3) })));

  console.log(`🔓 Open candidates (top ${((openCount/allPatterns.length)*100).toFixed(0)}%): ${openCandidates.length}`);
  console.log(`   Range: ${openCandidates[0]?.contextWinRate.toFixed(3)} - ${openCandidates[openCandidates.length-1]?.contextWinRate.toFixed(3)}`);
  console.log(`   Patterns:`, openCandidates.map(c => ({ pattern: c.pattern, contextWR: c.contextWinRate?.toFixed(3) })));

  // Fallback if somehow still no candidates
  if (lockCandidates.length === 0 || openCandidates.length === 0) {
    console.info(`ℹ️  Using fallback: ${lockCandidates.length === 0 ? 'no distinct lock patterns' : ''} ${openCandidates.length === 0 ? 'no distinct open patterns' : ''}`);
    console.info('   → Testing all patterns for maximum coverage');
    
    const fallbackPatterns = allPatterns.slice(0, 10);
    const pairs = generateAllPairs(fallbackPatterns);
    const pairCombos = generatePairCombinations(pairs, comboSize, 500);
    
    return pairCombos.map((combo, idx) => 
      combo.map((pair, pairIdx) => ({
        lock: pair.lock,
        open: pair.open,
        priority: pair.lock.length + pair.open.length
      }))
    );
  }
  
  // Step 3: Generate all lock/open pairs
  const pairs: Array<{ lock: string; open: string }> = [];
  for (const lockStat of lockCandidates) {
    for (const openStat of openCandidates) {
      pairs.push({
        lock: lockStat.pattern,
        open: openStat.pattern
      });
    }
  }
  
  console.log(`🔄 Generated ${pairs.length} lock/open pairs`);
  
  // Step 4: Generate combinations of pairs (with unique lock constraint)
  const maxCombos = 2000;
  const pairCombinations = generatePairCombinations(pairs, comboSize, maxCombos);
  
  console.log(`✅ Generated ${pairCombinations.length} VALID combos (unique locks only)`);
  
  // Validation: verify no duplicates
  const invalidCombos = pairCombinations.filter(combo => !hasUniqueLocks(combo));
  if (invalidCombos.length > 0) {
    console.error(`❌ CRITICAL: Found ${invalidCombos.length} invalid combos with duplicate locks!`);
    console.error('Sample invalid combo:', invalidCombos[0]);
  }
  
  // Step 5: Convert to PatternRule[][] with priority
  const combos: PatternRule[][] = pairCombinations.map(combo => 
    combo.map((pair, idx) => ({
      lock: pair.lock,
      open: pair.open,
      priority: pair.lock.length + pair.open.length // Longer patterns = higher priority
    }))
  );
  
  return combos;
}

// ============================================================================
// PHASE 3: SIMULATION ENGINE
// ============================================================================

export function simulateCombo(
  combo: PatternRule[],
  signalHistory: SignalHistoryItem[]
): SimulationResult {
  let mode: 'OPEN' | 'GATE' = 'OPEN';
  let activeLock: PatternRule | null = null;
  let recentResults = '';
  const trades: SimulationStep[] = [];
  const allSteps: SimulationStep[] = [];
  let lockActivations = 0;
  let skippedSignals = 0;
  
  // Sort combo by priority
  const sortedCombo = [...combo].sort((a, b) => a.priority - b.priority);
  
  for (let idx = 0; idx < signalHistory.length; idx++) {
    const signal = signalHistory[idx];
    
    // Only process EXIT signals with win data
    if (signal.win === null || (signal.action !== 'EXIT_LONG' && signal.action !== 'EXIT_SHORT')) {
      continue;
    }
    
    // Check LOCK patterns (if currently OPEN)
    if (mode === 'OPEN') {
      let matchedLock: PatternRule | null = null;
      
      for (const rule of sortedCombo) {
        if (recentResults.endsWith(rule.lock)) {
          matchedLock = rule;
          break;
        }
      }
      
      if (matchedLock) {
        mode = 'GATE';
        activeLock = matchedLock;
        lockActivations++;
        
        allSteps.push({
          signalIndex: idx,
          recentResults,
          mode: 'GATE',
          activeLock: matchedLock,
          action: 'LOCK',
          signal
        });
        
        // Update recent results with current signal's result
        recentResults += signal.win.toString();
        recentResults = recentResults.slice(-10); // Keep last 10
        continue; // Don't trade on lock signal
      }
    }
    
    // Check OPEN patterns (if currently GATE)
    if (mode === 'GATE' && activeLock) {
      if (recentResults.endsWith(activeLock.open)) {
        mode = 'OPEN';
        
        allSteps.push({
          signalIndex: idx,
          recentResults,
          mode: 'OPEN',
          activeLock: null,
          action: 'UNLOCK',
          signal
        });
        
        activeLock = null;
        
        // Update recent results
        recentResults += signal.win.toString();
        recentResults = recentResults.slice(-10);
        continue; // Don't trade on unlock signal
      } else {
        // Still in GATE mode - skip this signal
        skippedSignals++;
        
        allSteps.push({
          signalIndex: idx,
          recentResults,
          mode: 'GATE',
          activeLock,
          action: 'SKIP',
          signal
        });
        
        // Update recent results
        recentResults += signal.win.toString();
        recentResults = recentResults.slice(-10);
        continue;
      }
    }
    
    // If we reach here, mode is OPEN - execute trade
    const step: SimulationStep = {
      signalIndex: idx,
      recentResults,
      mode: 'OPEN',
      activeLock: null,
      action: 'TRADE',
      win: signal.win,
      signal
    };
    
    trades.push(step);
    allSteps.push(step);
    
    // Update recent results
    recentResults += signal.win.toString();
    recentResults = recentResults.slice(-10);
  }
  
  // Calculate win rate
  const wins = trades.filter(t => t.win === 1).length;
  const losses = trades.filter(t => t.win === 0).length;
  const totalTrades = wins + losses;
  const winRate = totalTrades > 0 ? (wins / totalTrades) * 100 : 0;
  
  return {
    winRate,
    wins,
    losses,
    totalTrades,
    trades,
    lockActivations,
    skippedSignals
  };
}

// ============================================================================
// PHASE 4: RANKING & SELECTION
// ============================================================================

export function rankCombos(
  combos: PatternRule[][],
  signalHistory: SignalHistoryItem[],
  onProgress?: (current: number, total: number) => void
): ComboSuggestion[] {
  const results: ComboSuggestion[] = [];
  
  for (let i = 0; i < combos.length; i++) {
    const combo = combos[i];
    const simulationResult = simulateCombo(combo, signalHistory);
    
    // Calculate confidence based on sample size
    const confidence = Math.min(100, (simulationResult.totalTrades / 30) * 100);
    
    // Filter out combos with insufficient data or extreme win rates
    if (
      simulationResult.totalTrades >= 10 &&
      simulationResult.winRate >= 40 &&
      simulationResult.winRate <= 80
    ) {
      results.push({
        rank: 0, // Will be assigned after sorting
        rules: combo,
        winRate: simulationResult.winRate,
        wins: simulationResult.wins,
        losses: simulationResult.losses,
        totalTrades: simulationResult.totalTrades,
        confidence,
        lockActivations: simulationResult.lockActivations,
        skippedSignals: simulationResult.skippedSignals,
        simulationResult
      });
    }
    
    if (onProgress) {
      onProgress(i + 1, combos.length);
    }
  }
  
  // Sort by win rate (primary), then by total trades (secondary)
  results.sort((a, b) => {
    if (Math.abs(a.winRate - b.winRate) > 1) {
      return b.winRate - a.winRate;
    }
    return b.totalTrades - a.totalTrades;
  });
  
  // Assign ranks
  results.forEach((result, index) => {
    result.rank = index + 1;
  });
  
  return results.slice(0, 20); // Return top 20
}

// ============================================================================
// MAIN ORCHESTRATION FUNCTION
// ============================================================================

export interface GenerateComboConfig {
  botId: string | null;
  timeRange: string;
  comboSize: number;
  patternMinLength: number;
  patternMaxLength: number;
  minFrequency: number;
  minWinRateTarget?: number;
}

export async function generatePatternComboSuggestions(
  config: GenerateComboConfig,
  onProgress?: (stage: string, progress: number, total: number) => void
): Promise<ComboSuggestion[]> {
  try {
    // Stage 1: Fetch signal history
    onProgress?.('Fetching signal history...', 1, 4);
    const signalHistory = await fetchSignalHistory(config.botId, config.timeRange);
    
    if (signalHistory.length < 50) {
      throw new Error('Insufficient signal history. Need at least 50 signals.');
    }
    
    // Stage 2: Analyze subsequences
    onProgress?.('Analyzing patterns...', 2, 4);
    const subsequenceStats = analyzeSubsequences(
      signalHistory,
      config.patternMinLength,
      config.patternMaxLength
    );
    
    // Stage 3: Generate combo candidates
    onProgress?.('Generating combo candidates...', 3, 4);
  const combos = generateComboCandidates(
    subsequenceStats,
    config.comboSize,
    config.minFrequency,
    config.patternMinLength,
    config.patternMaxLength
  );
    
    if (combos.length === 0) {
      throw new Error('Could not generate any valid combos. Try adjusting parameters.');
    }
    
    // Stage 4: Simulate and rank combos
    onProgress?.('Simulating combos...', 4, 4);
    const rankedCombos = rankCombos(combos, signalHistory, (current, total) => {
      onProgress?.(`Simulating combo ${current}/${total}...`, 4, 4);
    });
    
    // Filter by minimum win rate target
    const minTarget = config.minWinRateTarget ?? 60;
    const filtered = rankedCombos.filter(c => c.winRate >= minTarget);
    
    console.log(`✅ Final results: ${filtered.length} combos with win rate >= ${minTarget}%`);
    
    return filtered.slice(0, 10);
  } catch (error) {
    console.error('Error generating combo suggestions:', error);
    throw error;
  }
}

// ============================================================================
// BOT PATTERN PERFORMANCE
// ============================================================================

export interface PatternPerformanceData {
  id: string;
  lock_pattern: string;
  open_pattern: string;
  priority: number;
  enabled: boolean;
  total_matches: number;
  forwards: number;
  skips: number;
  wins: number;
  losses: number;
  win_rate: number | null;
  lock_count: number;
  open_count: number;
  
  // Overall combo stats (only present in first pattern)
  overall_win_rate?: number | null;
  overall_forwards?: number;
  overall_wins?: number;
  overall_losses?: number;
}

/**
 * Simulate flexible pattern combo logic to calculate accurate performance
 * This simulates the EXACT Type 2 behavior: GATE/LOCK/OPEN states
 */
function simulateFlexiblePatternPerformance(
  patterns: PatternRule[],
  signals: SignalHistoryItem[]
): {
  patternStats: Map<string, {
    wins: number;
    losses: number;
    forwards: number;
    skips: number;
    lockCount: number;
    openCount: number;
  }>;
  overallStats: {
    totalForwards: number;
    totalWins: number;
    totalLosses: number;
    winRate: number | null;
  };
} {
  const patternStats = new Map<string, {
    wins: number;
    losses: number;
    forwards: number;
    skips: number;
    lockCount: number;
    openCount: number;
  }>();

  // Initialize stats for each pattern
  for (const pattern of patterns) {
    const key = `${pattern.lock}|${pattern.open}|${pattern.priority}`;
    patternStats.set(key, {
      wins: 0,
      losses: 0,
      forwards: 0,
      skips: 0,
      lockCount: 0,
      openCount: 0
    });
  }

  let mode: 'OPEN' | 'GATE' = 'OPEN';
  let activeLock: PatternRule | null = null;
  let recentResults = '';
  
  // Track overall stats
  let overallForwards = 0;
  let overallWins = 0;
  let overallLosses = 0;
  
  // Sort patterns by priority
  const sortedPatterns = [...patterns].sort((a, b) => a.priority - b.priority);

  for (const signal of signals) {
    // Only process EXIT signals with win data
    if (signal.win === null || (signal.action !== 'EXIT_LONG' && signal.action !== 'EXIT_SHORT')) {
      continue;
    }

    // ===== STEP 1: TRADE FIRST (if mode is OPEN) =====
    const shouldForward = (mode === 'OPEN');
    
    if (shouldForward) {
      // FORWARD this signal
      overallForwards++;
      if (signal.win === 1) {
        overallWins++;
      } else if (signal.win === 0) {
        overallLosses++;
      }
    } else {
      // SKIP this signal (mode is GATE)
      if (activeLock) {
        const key = `${activeLock.lock}|${activeLock.open}|${activeLock.priority}`;
        const stats = patternStats.get(key);
        if (stats) {
          stats.skips++;
        }
      }
    }
    
    // ===== STEP 2: UPDATE recentResults AFTER the trade =====
    recentResults += signal.win.toString();
    recentResults = recentResults.slice(-10);
    
    // ===== STEP 3: CHECK patterns with NEW recentResults =====
    // (This determines the mode for the NEXT signal)
    
    if (mode === 'OPEN') {
      // Check if any LOCK pattern matches
      let matchedLock: PatternRule | null = null;
      
      for (const rule of sortedPatterns) {
        if (recentResults.endsWith(rule.lock)) {
          matchedLock = rule;
          break;
        }
      }
      
      if (matchedLock) {
        // Pattern matched for LOCK - activate GATE mode for NEXT signal
        mode = 'GATE';
        activeLock = matchedLock;
        
        const key = `${matchedLock.lock}|${matchedLock.open}|${matchedLock.priority}`;
        const stats = patternStats.get(key);
        if (stats) {
          stats.lockCount++;
        }
      }
    } else if (mode === 'GATE' && activeLock) {
      // Check if OPEN pattern matches
      if (recentResults.endsWith(activeLock.open)) {
        // Pattern matched for OPEN - return to OPEN mode for NEXT signal
        mode = 'OPEN';
        
        const key = `${activeLock.lock}|${activeLock.open}|${activeLock.priority}`;
        const stats = patternStats.get(key);
        if (stats) {
          stats.openCount++;
        }
        
        activeLock = null;
      }
    }
  }

  const overallWinRate = (overallWins + overallLosses) > 0 
    ? (overallWins / (overallWins + overallLosses)) * 100 
    : null;

  return {
    patternStats,
    overallStats: {
      totalForwards: overallForwards,
      totalWins: overallWins,
      totalLosses: overallLosses,
      winRate: overallWinRate
    }
  };
}

export async function fetchBotPatternPerformance(
  settingId: string,
  botId: string,
  timeRange: string
): Promise<PatternPerformanceData[]> {
  // Calculate start date based on time range
  const now = new Date();
  let startDate = new Date();
  switch (timeRange) {
    case 'today':
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      break;
    case '7days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case '30days':
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
    default:
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  }

  // Fetch patterns
  const { data: patterns, error: patternsError } = await supabase
    .from('flexible_skip_patterns')
    .select('*')
    .eq('setting_id', settingId)
    .order('priority', { ascending: true });

  if (patternsError) {
    console.error('Error fetching patterns:', patternsError);
    throw patternsError;
  }

  if (!patterns || patterns.length === 0) {
    return [];
  }

  // Fetch ALL raw EXIT signals for this bot in the time range
  const { data: allSignals, error: signalsError } = await supabase
    .from('conditional_skip_events')
    .select('win, action, created_at')
    .eq('bot_id', botId)
    .in('action', ['EXIT_LONG', 'EXIT_SHORT'])
    .gte('created_at', startDate.toISOString())
    .order('created_at', { ascending: true });

  if (signalsError) {
    console.error('Error fetching signals:', signalsError);
    throw signalsError;
  }

  if (!allSignals || allSignals.length === 0) {
    // No signals - return patterns with zero stats
    return patterns.map(pattern => ({
      id: pattern.id,
      lock_pattern: pattern.lock_pattern,
      open_pattern: pattern.open_pattern,
      priority: pattern.priority,
      enabled: pattern.enabled,
      total_matches: 0,
      forwards: 0,
      skips: 0,
      wins: 0,
      losses: 0,
      win_rate: null,
      lock_count: 0,
      open_count: 0
    }));
  }

  // Convert patterns to PatternRule format for simulation
  const patternRules: PatternRule[] = patterns.map(p => ({
    lock: p.lock_pattern,
    open: p.open_pattern,
    priority: p.priority
  }));

  // Simulate the combo logic
  const { patternStats: simulationStats, overallStats } = simulateFlexiblePatternPerformance(
    patternRules,
    allSignals as SignalHistoryItem[]
  );

  // Build performance data from simulation results
  const performanceData: PatternPerformanceData[] = patterns.map(pattern => {
    const key = `${pattern.lock_pattern}|${pattern.open_pattern}|${pattern.priority}`;
    const stats = simulationStats.get(key) || {
      wins: 0,
      losses: 0,
      forwards: 0,
      skips: 0,
      lockCount: 0,
      openCount: 0
    };

    const totalTrades = stats.wins + stats.losses;
    const winRate = totalTrades > 0 ? (stats.wins / totalTrades) * 100 : null;
    const totalMatches = stats.lockCount + stats.openCount;

    return {
      id: pattern.id,
      lock_pattern: pattern.lock_pattern,
      open_pattern: pattern.open_pattern,
      priority: pattern.priority,
      enabled: pattern.enabled,
      total_matches: totalMatches,
      forwards: stats.forwards,
      skips: stats.skips,
      wins: stats.wins,
      losses: stats.losses,
      win_rate: winRate,
      lock_count: stats.lockCount,
      open_count: stats.openCount
    };
  });

  // Add overall stats to first pattern
  if (performanceData.length > 0) {
    performanceData[0].overall_win_rate = overallStats.winRate;
    performanceData[0].overall_forwards = overallStats.totalForwards;
    performanceData[0].overall_wins = overallStats.totalWins;
    performanceData[0].overall_losses = overallStats.totalLosses;
  }

  return performanceData;
}

// ============================================================================
// APPLY COMBO TO SETTINGS
// ============================================================================

/**
 * Ensures that a conditional_skip_settings record exists for the bot
 * Creates one if missing
 */
async function ensureSettingExists(botId: string): Promise<string> {
  // Check if setting already exists
  const { data: existingSetting, error: fetchError } = await supabase
    .from('conditional_skip_settings')
    .select('id')
    .eq('bot_id', botId)
    .eq('skip_type', 'FLEXIBLE_PATTERN')
    .maybeSingle();
  
  if (fetchError) {
    console.error('Error checking for existing setting:', fetchError);
    throw fetchError;
  }

  // Return existing setting ID if found
  if (existingSetting?.id) {
    return existingSetting.id;
  }

  // Create new setting if not found
  console.log(`Creating new conditional_skip_settings for bot ${botId}`);
  const { data: newSetting, error: createError } = await supabase
    .from('conditional_skip_settings')
    .insert({
      bot_id: botId,
      skip_type: 'FLEXIBLE_PATTERN',
      enabled: true
    })
    .select('id')
    .single();

  if (createError) {
    console.error('Error creating setting:', createError);
    throw createError;
  }

  if (!newSetting) {
    throw new Error('Failed to create setting');
  }

  console.log(`Created new setting with ID: ${newSetting.id}`);
  return newSetting.id;
}

export async function applyComboToSettings(
  settingId: string,
  combo: ComboSuggestion
): Promise<void> {
  try {
    // Delete existing patterns for this setting
    const { error: deleteError } = await supabase
      .from('flexible_skip_patterns')
      .delete()
      .eq('setting_id', settingId);
    
    if (deleteError) throw deleteError;
    
    // Insert new patterns
    const newPatterns = combo.rules.map(rule => ({
      setting_id: settingId,
      lock_pattern: rule.lock,
      open_pattern: rule.open,
      priority: rule.priority,
      enabled: true
    }));
    
    const { error: insertError } = await supabase
      .from('flexible_skip_patterns')
      .insert(newPatterns);
    
    if (insertError) throw insertError;
    
    console.log(`Successfully applied ${newPatterns.length} pattern rules to setting ${settingId}`);
  } catch (error) {
    console.error('Error applying combo to settings:', error);
    throw error;
  }
}

/**
 * Apply combo to bot - auto-creates settings if needed
 */
export async function applyComboToBot(
  botId: string,
  combo: ComboSuggestion
): Promise<void> {
  try {
    // Ensure setting exists (auto-create if needed)
    const settingId = await ensureSettingExists(botId);
    
    // Apply combo to the setting
    await applyComboToSettings(settingId, combo);
  } catch (error) {
    console.error('Error applying combo to bot:', error);
    throw error;
  }
}
